import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env';
import { BehaviorSubject, Observable, Subject, interval, firstValueFrom, EMPTY } from 'rxjs';
import { filter, map, tap, switchMap, catchError, distinctUntilChanged } from 'rxjs/operators';
import { LoggingService, LogSeverity } from '@core/services/logging.service';

/* ──────────────── Public contract ──────────────── */
export interface VersionInfo {
  currentVersion: string;
  latestVersion: string;
  isLatest: boolean;
}

interface VersionResponse {
  version: string;
}

const packageJson = require('../../../../package.json');

@Injectable({ providedIn: 'root' })
export class VersionService {
  private readonly buildVersion = packageJson.version as string;
  private readonly POLL_INTERVAL_MS = 60_000;
  private readonly COOLDOWN_MS = 10_000;

  private readonly _versionInfo$ = new BehaviorSubject<VersionInfo | null>(null);
  readonly versionInfo$ = this._versionInfo$.asObservable();

  private readonly _newVersion$ = new Subject<VersionInfo>();
  readonly newVersion$ = this._newVersion$.asObservable();

  private lastEmitTs = 0;
  private pendingTimer: number | NodeJS.Timeout | null = null;

  constructor(private http: HttpClient, private logger: LoggingService) {
    const initNotice = `FreshAi build version ${this.buildVersion}`;
    this.logger.writeLog(`[VersionService] ${initNotice}`, {
      severity: LogSeverity.NOTICE,
    });

    this.initAsciiArt(initNotice);
    this.initStream();
    this.startAutoPoll();
  }

  get versionInfo(): VersionInfo | null {
    return this._versionInfo$.value;
  }

  load(): Promise<void> {
    return firstValueFrom(this.fetchVersion()).then(() => void 0);
  }

  refresh(): Observable<VersionInfo> {
    return this.fetchVersion();
  }

  reloadNow(): void {
    if (environment.name == 'local') {
      //console.log('[VersionService] Reload cancelled')
      return;
    }
    location.reload();
  }

  private fetchVersion(): Observable<VersionInfo> {
    const ts = Date.now();
    return this.http.get<VersionResponse>(`/version.json?ts=${ts}`).pipe(
      map(({ version }) => ({
        currentVersion: this.buildVersion,
        latestVersion: version,
        isLatest: version === this.buildVersion,
      })),
      tap((info) => {
        this._versionInfo$.next(info); // always update state
        this.logger.writeLog('[VersionService] Version info', {
          severity: LogSeverity.INFO,
          detail: {
            ...info,
          },
        });

        // const previous = this._versionInfo$.value
        // const changed = !!previous && previous.latestVersion !== info.latestVersion
        // if (changed) {
        //   this.logger.writeLog('[VersionService] Application version update', {
        //     ...info,
        //     severity: LogSeverity.INFO,
        //   });
        // }
      }),
    );
  }

  private startAutoPoll(): void {
    interval(this.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.refresh().pipe(
            catchError((err) => {
              this.logger.writeLog('[VersionService] Poll failed', {
                err,
                severity: LogSeverity.WARNING,
              });
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  private initStream(): void {
    // // Immediate check on bootstrap
    // this.versionInfo$
    //   .pipe(
    //     filter((v): v is VersionInfo => !!v && !v.isLatest),
    //     take(1),
    //   )
    //   .subscribe((v) => this.maybeEmit(v))

    // Ongoing changes
    this.versionInfo$
      .pipe(
        filter((v): v is VersionInfo => !!v),
        distinctUntilChanged((a, b) => a.latestVersion === b.latestVersion),
        filter((v) => !v.isLatest),
      )
      .subscribe((v) => this.maybeEmit(v));
  }

  private maybeEmit(v: VersionInfo): void {
    const now = Date.now();
    const delta = now - this.lastEmitTs;

    if (delta >= this.COOLDOWN_MS) {
      this.emit(v);
    } else if (!this.pendingTimer) {
      const wait = this.COOLDOWN_MS - delta;
      this.logger.writeLog('[VersionService] Emission deferred by cool-down', {
        wait,
        severity: LogSeverity.DEBUG,
      });
      this.pendingTimer = setTimeout(() => {
        this.pendingTimer = null;
        this.emit(v);
      }, wait);
    }
  }

  private emit(v: VersionInfo): void {
    this.lastEmitTs = Date.now();
    this.logger.writeLog('[VersionService] Application build version update', {
      ...v,
      severity: LogSeverity.INFO,
    });
    this._newVersion$.next(v);
  }

  private initAsciiArt(notice: string): void {
    const art = `
     %c
     ####                                                        #####                               
     #########                                               #########                               
     ##############                                      #############                               
     ##################                             ##################                               
     ######################                      #####################                               
     ######## ##################            ################  ########                               
     #######      ###########           ###############        #######                               
     #######           ###          ################           #######                               
     #######                    ###############                #######                               
     #######                ################                   #######                               
     #######            ###############          ##            #######                               
     #######        ###############           ##########       #######                               
     ########   ###############            ################## ########                               
     ######################                    #######################                               
     ##################                            ###################                               
     ##############                                     ##############                               
     ##########                                             ##########                               
     #####                                                       #####     

     %c  ${notice}
     `;

    Object.defineProperty(window, 'version', {
      get: () => {
        console.info(art, 'color:#009cd4;font-size:.8em;', 'color:#0c0;font-size:1.5em'); // eslint-disable-line no-console
      },
      configurable: true,
    });
  }
}
